import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import { useTenant } from 'context/TenantContext';
import useEscapeClose from 'hooks/useEscapeClose';
import { useAuth } from 'context/AuthContext';
import ImportPointageModale from './ImportPointageModale';
import { formatDate, aujourdHui } from '../../../../utils/jour';

/* ═══════════════════════════════════════════════════════════════════════════
 * LES SIX STATUTS DE POINTAGE — CONVENTION P/R/A (lot « paie-pointage », 05/10/2026)
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `repos` AJOUTE ICI PARCE QU IL MANQUAIT PARTOUT. La contrainte de base
 *    n admettait que cinq statuts, et le « R » de la convention P/R/A — le repos
 *    hebdomadaire — n en faisait pas partie. Un jour de repos ne pouvait donc
 *    etre saisi que comme `present`, ce qui est FAUX, ou laisse vide, ce qui est
 *    indistinguable d un jour non pointe. Le moteur de paie, lui, parlait deja
 *    P/R/A (`calculPur.js:320-321`). **La migration 088 ouvre la contrainte, et
 *    cette ligne est ce qui rend le statut atteignable** : *une valeur admise par
 *    la base et absente de l ecran est une valeur que personne ne peut saisir.*
 *
 * ⚠️ CE QUE CHAQUE STATUT VAUT A LA PAIE — classification dans
 *    `backend/src/services/paie/chargeurPaie.js` (STATUTS_POINTAGE), et nulle
 *    part ailleurs :
 *      present · repos · retard   -> PRESENCES PAYEES (un retard est travaille)
 *      conge   · maladie          -> absences JUSTIFIEES, non deduites
 *      absent                     -> SEULE absence deduite du salaire
 * ⓘ L ordre de ce litteral est celui des selecteurs a l ecran : P, R, A d abord
 *   — les trois de la convention, les plus saisis — puis les trois autres.
 */
/* ⓘ `permission` : un bleu-vert distinct du rouge d `absent`. *Deux motifs qui
 *   ne se sanctionnent pas pareil ne doivent pas se ressembler à l écran.* */
const STATUT_COLORS = { present: '#2e7d32', repos: '#546e7a', absent: '#e53935', retard: '#f57c00', conge: '#1565c0', maladie: '#7b1fa2', permission: '#00838f', demission: '#5d4037' };
/* ⚠️ ACCENTS RETABLIS LE 05/10/2026. Ces libelles sont lus par un utilisateur,
 *    a l ecran ET sur la fiche de pointage imprimee : « Present » et « Conge »
 *    s ecrivent « Présent » et « Congé ». *Un texte vu par quelqu un s ecrit en
 *    francais correct, meme quand la consigne qui l a demande ne portait pas ses
 *    accents.* (Les identifiants, eux, restent sans accent : ce sont des valeurs
 *    de base, pas du texte.) */
/* ⛔ LE LIBELLÉ DIT LE STATUT, JAMAIS SON EFFET SUR LA PAIE (Dom, 06/10/2026).
 *    L'effet se lit au référentiel du pays et à la surcharge du tenant —
 *    `GET /pointages/effets` — et s'affiche À CÔTÉ, ou « à renseigner ». */
const STATUT_LABELS = { present: 'Présent', repos: 'Repos', absent: 'Absent', retard: 'Retard', conge: 'Congé', maladie: 'Maladie', permission: 'Permission', demission: 'Démission' };
const LIBELLE_EFFET = { paye: 'payé', deduit: 'déduit', regime_conges: 'régime de congés', regime_maladie: 'régime de maladie', sortie: 'événement de sortie' };
const JOURS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];


function formatTime(d) { return d ? new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '-'; }
function todayStr() { return aujourdHui(); }

function getMonthDays(year, month) {
  const days = [];
  const d = new Date(year, month, 1);
  while (d.getMonth() === month) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

export default function PointagePage() {
  // IDENTITE TENANT — ce document est REMIS AU CLIENT ou au salarie. Il portait
  // « GLADIATOR SECURITY SYSTEMS » EN DUR : l'identite de l'EDITEUR imprimee sur
  // le document d'un tenant. Meme famille que les 4 fuites corrigees en juillet-aout
  // sur les PDF backend, mais cote frontend — non couverte par la matrice du garde-fou.
  const tenant = useTenant();
  const auth = useAuth();

  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sites, setSites] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const [showImport, setShowImport] = useState(false);
  /* ⛔ L IMPORT EST PLUS ETROIT QUE LA SAISIE — rh, dg, admin SEULS (lot C).
   *    `cos` et `superviseur_general` pointent leur equipe jour par jour, et
   *    c est leur metier ; importer un classeur ecrit le mois ENTIER de TOUT
   *    l effectif et peut REMPLACER ce qui existe — c est un acte RH.
   * ⚠️ MEME LISTE QUE `canImporter` COTE API (`pointageRoutes`), et c est la
   *    condition pour que l ecran ne montre pas un bouton que le serveur
   *    refuserait en 403. *Un bouton propose a qui ne peut pas l utiliser est
   *    une promesse que le serveur dementira.* */
  const rolesDuCompte = (auth.user && auth.user.roles) || [];
  const peutImporter = ['admin', 'dg', 'rh'].some((r) => rolesDuCompte.includes(r));
  /* ⓘ Miroir EXACT du garde serveur de la sortie (`agentRoutes`) : un bouton
   *   offert à un rôle que le serveur refuse promet un acte qui échouera. */
  const peutEnregistrerSortie = ['admin', 'dg', 'rh'].some((r) => rolesDuCompte.includes(r));
  const [bulkSite, setBulkSite] = useState('');
  const [bulkRows, setBulkRows] = useState([]);
  const [, setFicheAgent] = useState(null);
  const [ficheData, setFicheData] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({ statut: '', heure_arrivee: '', heure_depart: '', observations: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [filterDate, setFilterDate] = useState(todayStr());
  const [filterSite, setFilterSite] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [viewMonth, setViewMonth] = useState(() => { const n = new Date(); return { year: n.getFullYear(), month: n.getMonth() }; });
  /* ⛔ L EFFET PAIE DE CHAQUE STATUT N EST PAS DANS CE FICHIER. Il vient de
   *    `GET /pointages/effets`, qui le lit au referentiel du PAYS du tenant et a
   *    la surcharge du tenant. *Jusqu au 06/10, les libelles de cet ecran
   *    affirmaient « absence justifiee, non deduite » — une regle de droit
   *    nationale, ecrite en dur dans un composant React, identique pour les 17
   *    pays.* L ecran affiche desormais ce que la base repond, ou dit qu il
   *    reste a renseigner. */
  const [effets, setEffets] = useState(null);
  /* ── LA DÉMISSION POINTÉE PROPOSE LA SORTIE, ET NE LA FAIT PAS ──────────────
   * ⛔ POINTER « Démission » N'ÉCRIT AUCUNE SORTIE. C'est un fait constaté sur
   *    la journée ; faire sortir l'agent de l'effectif est un acte distinct,
   *    confirmé par une personne habilitée, et tracé. *Un code dans une case ne
   *    met pas fin à un contrat de travail.*
   * ⓘ La confirmation passe par le MÊME point d'écriture que l'écran de sortie
   *   (file 100) — `DELETE /agents/:id` avec `date_sortie` et `motif_sortie_id`.
   *   Un second chemin d'écriture pour la même décision produirait deux
   *   comportements à maintenir, et un seul serait tenu à jour. */
  const [sortieAConfirmer, setSortieAConfirmer] = useState(null);
  const [motifsSortie, setMotifsSortie] = useState(null);
  const [sortieMotif, setSortieMotif] = useState('');
  const [sortieErreur, setSortieErreur] = useState('');
  const [sortieFaite, setSortieFaite] = useState('');
  const [editRowAgent, setEditRowAgent] = useState(null);
  const [sortiesEnAttente, setSortiesEnAttente] = useState([]);

  useEscapeClose(showBulk, () => setShowBulk(false));

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const mois = Number(String(filterDate).slice(5, 7));
      const annee = Number(String(filterDate).slice(0, 4));
      const [pRes, aRes, sRes, eRes] = await Promise.all([
        api.get('/pointages', { params: { date: filterDate } }).catch(() => ({ data: [] })),
        api.get('/agents').catch(() => ({ data: [] })),
        api.get('/sites').catch(() => ({ data: [] })),
        /* ⓘ `null` EN CAS D ECHEC, PAS UN OBJET VIDE : « on n a pas pu lire le
         *   referentiel » et « le referentiel ne dit rien » ne s affichent pas
         *   pareil, et seul le second est une consigne de saisie. */
        api.get('/pointages/effets', { params: { mois, annee } }).catch(() => ({ data: null })),
      ]);
      setItems(pRes.data);
      setAgents(aRes.data);
      setSites(sRes.data);
      setEffets(eRes.data);
    } catch {} finally { setLoading(false); }
  }, [filterDate]);

  useEffect(() => { load(); }, [load]);

  const openBulk = () => {
    setBulkSite('');
    setBulkRows([]);
    setError('');
    setShowBulk(true);
  };

  const loadBulkAgents = () => {
    const activeAgents = agents.filter(a => a.statut === 'actif');
    setBulkRows(activeAgents.map(a => ({
      agent_id: a.id, nom: a.nom, prenom: a.prenom, matricule: a.matricule,
      statut: 'present', heure_arrivee: '06:00', heure_depart: '',
    })));
  };

  const setBulkField = (idx, field, value) => {
    setBulkRows(rows => rows.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const markAllPresent = () => {
    setBulkRows(rows => rows.map(r => ({ ...r, statut: 'present' })));
  };

  const handleBulkSubmit = async () => {
    setSaving(true); setError('');
    try {
      await api.post('/pointages/bulk', {
        date_pointage: filterDate,
        site_id: bulkSite || null,
        pointages: bulkRows.map(r => ({
          agent_id: r.agent_id, statut: r.statut,
          heure_arrivee: r.heure_arrivee || null, heure_depart: r.heure_depart || null,
        })),
      });
      setShowBulk(false); load();
      /* ⓘ UNE seule confirmation à la fois, et en file : la sortie est une
       *   décision individuelle. *Confirmer d'un geste la sortie de plusieurs
       *   agents, c'est exactement ce que ce lot refuse à l'import.* */
      const dems = bulkRows.filter((r) => r.statut === 'demission');
      if (dems.length > 0) {
        setSortiesEnAttente(dems.slice(1).map((r) => ({
          agentId: r.agent_id, matricule: matriculeDe(r.agent_id), date: filterDate,
        })));
        await proposerSortie(dems[0].agent_id, matriculeDe(dems[0].agent_id), filterDate);
      }
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); } finally { setSaving(false); }
  };

  const openEditRow = (p) => {
    setEditRow(p.id);
    setEditRowAgent({ agentId: p.agent_id, date: p.date_pointage });
    setEditForm({
      statut: p.statut || 'present',
      heure_arrivee: p.heure_arrivee || '',
      heure_depart: p.heure_depart || '',
      observations: p.observations || '',
    });
  };

  /* ⚠️ APPELÉE APRÈS L'ÉCRITURE DU POINTAGE, jamais avant : on ne propose une
   *    sortie que sur un fait déjà enregistré. */
  const proposerSortie = async (agentId, matricule, date) => {
    if (!peutEnregistrerSortie) return;
    setSortieErreur(''); setSortieFaite('');
    setSortieAConfirmer({ agentId, matricule, date });
    setSortieMotif('');
    try {
      const { data } = await api.get('/agents/motifs-sortie');
      setMotifsSortie(data.motifs || []);
      /* ⓘ On PRÉ-SÉLECTIONNE le motif de démission s'il existe dans le droit du
       *   pays, et on ne l'invente pas s'il n'y est pas : le référentiel décide
       *   quels motifs sont applicables, pas cet écran. */
      const dem = (data.motifs || []).find((m) => m.code === 'demission');
      if (dem) setSortieMotif(dem.code);
    } catch (err) {
      /* Aucun repli sur une liste en dur : sans référentiel, rien n'est proposé
       * et la phrase du serveur s'affiche telle quelle. */
      setMotifsSortie([]);
      setSortieErreur(err?.response?.data?.error
        || 'Les motifs de sortie n’ont pas pu être lus. La sortie ne peut pas être enregistrée.');
    }
  };

  const confirmerSortie = async () => {
    setSaving(true); setSortieErreur(''); setSortieFaite('');
    try {
      const { data } = await api.delete(`/agents/${sortieAConfirmer.agentId}`, {
        data: { date_sortie: sortieAConfirmer.date, motif_sortie_id: sortieMotif },
      });
      setSortieFaite(`Sortie enregistrée : ${sortieAConfirmer.matricule}, le `
        + `${formatDate(sortieAConfirmer.date)}.${data.alerte ? ` ${data.alerte}` : ''}`);
      setSortieAConfirmer(null);
      load();
      if (sortiesEnAttente.length > 0) {
        const [suivant, ...reste] = sortiesEnAttente;
        setSortiesEnAttente(reste);
        await proposerSortie(suivant.agentId, suivant.matricule, suivant.date);
      }
    } catch (err) {
      setSortieErreur(err?.response?.data?.error
        || 'La sortie n’a pas été enregistrée.');
    } finally { setSaving(false); }
  };

  const matriculeDe = (agentId) => {
    const a = agents.find((x) => x.id === agentId);
    /* ⓘ Le matricule désigne l'agent dans tout ce qui se trace (règle 63) ; à
     *   défaut on le DIT, on n'affiche pas un identifiant technique nu. */
    return a ? (a.matricule || 'matricule non attribué') : 'agent inconnu';
  };

  const saveEditRow = async () => {
    setSaving(true); setError('');
    try {
      await api.put(`/pointages/${editRow}`, editForm);
      const r = editRowAgent;
      setEditRow(null);
      load();
      /* ⛔ APRÈS l'écriture du pointage, et SANS rien écrire d'autre : le fait est
       *    enregistré, la sortie n'est que PROPOSÉE. */
      if (editForm.statut === 'demission' && r) {
        await proposerSortie(r.agentId, matriculeDe(r.agentId), r.date);
      }
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); } finally { setSaving(false); }
  };

  const deletePointage = async (p) => {
    const name = agentName(p.agent_id);
    if (!window.confirm(`Annuler le pointage de ${name} ?`)) return;
    try {
      await api.delete(`/pointages/${p.id}`);
      setEditRow(null);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
  };

  const handleExport = () => {
    const headers = ['Agent', 'Heure arrivee', 'Heure depart', 'Site', 'Statut'];
    const rows = filtered.map(p => [
      agentName(p.agent_id), formatTime(p.heure_arrivee), formatTime(p.heure_depart),
      siteName(p.site_id), STATUT_LABELS[p.statut] || p.statut,
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `pointage_${filterDate}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const agentName = (id) => { const a = agents.find(x => x.id === id); return a ? `${a.prenom} ${a.nom}` : '-'; };
  const siteName = (id) => { const s = sites.find(x => x.id === id); return s ? s.nom : '-'; };

  const openFiche = async (agentId) => {
    try {
      const mois = viewMonth.month + 1;
      const annee = viewMonth.year;
      const res = await api.get(`/pointages/mensuel/${agentId}`, { params: { mois, annee } });
      setFicheData(res.data);
      setFicheAgent(agentId);
    } catch { setError('Erreur chargement fiche'); }
  };

  const exportFichePdf = () => {
    if (!ficheData) return;
    /* ⚠️ TROISIEME COPIE DE LA MEME LISTE, SUPPRIMEE LE 05/10/2026. Ce fichier
     *    declarait les statuts a trois endroits : STATUT_COLORS, STATUT_LABELS et
     *    un `STATUT_EMOJI` local, identique au second. En ajoutant `repos` je
     *    l aurais oublie ici, et la fiche de pointage IMPRIMEE aurait affiche
     *    « repos » en minuscule brute a cote de « Présent » — le defaut se voit
     *    seulement sur un papier, donc tard. *Une liste recopiee trois fois
     *    diverge a la premiere valeur ajoutee.* La fiche lit desormais la MEME
     *    source que l ecran. */
    const STATUT_EMOJI = STATUT_LABELS;
    const MOIS_FR = ['', 'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
    const JOUR_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const a = ficheData.agent;
    const r = ficheData.recap;
    const w = window.open('', '_blank', 'width=900,height=700');
    w.document.write(`<html><head><title>Fiche pointage ${a.prenom} ${a.nom}</title>
      <style>body{font-family:Arial,sans-serif;padding:30px;font-size:12px}
      table{width:100%;border-collapse:collapse;margin:16px 0}
      th,td{border:1px solid #ccc;padding:6px 8px;text-align:center;font-size:11px}
      th{background:#f5f5f5;font-weight:600}
      .present{color:#2e7d32}.repos{color:#546e7a}.absent{color:#e53935}.retard{color:#f57c00}.conge{color:#1565c0}.maladie{color:#7b1fa2}.permission{color:#00838f}.demission{color:#5d4037}
      .header{display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #8B1A1A;padding-bottom:12px;margin-bottom:16px}
      .sig{display:flex;justify-content:space-between;margin-top:40px}
      .sig div{width:30%;text-align:center;border-top:1px solid #333;padding-top:8px;font-size:11px}
      @media print{body{padding:20px}}
      </style></head><body>
      <div class="header">
        <div><strong style="font-size:16px;color:#8B1A1A">${tenant?.nom || ''}</strong>${tenant?.rccm ? `<br/>RCCM ${tenant.rccm}` : ''}</div>
        <div style="text-align:right"><strong>FICHE DE POINTAGE</strong><br/>${MOIS_FR[ficheData.mois]} ${ficheData.annee}</div>
      </div>
      <div style="display:flex;gap:40px;margin-bottom:12px">
        <div><strong>Agent :</strong> ${a.prenom} ${a.nom}</div>
        <div><strong>Matricule :</strong> ${a.matricule}</div>
        <div><strong>Poste :</strong> ${a.poste || a.specialite || '-'}</div>
      </div>
      <table>
        <thead><tr><th>Jour</th><th>Date</th><th>Arrivee</th><th>Depart</th><th>Heures</th><th>Statut</th></tr></thead>
        <tbody>
        ${ficheData.jours.map(j => `<tr${j.dimanche ? ' style="background:#f9f9f9"' : ''}>
          <td>${j.jour}</td><td>${JOUR_FR[j.dow]}</td>
          <td>${j.arrivee || '-'}</td><td>${j.depart || '-'}</td>
          <td>${j.heures || '-'}</td>
          <td class="${j.statut || ''}">${j.statut ? STATUT_EMOJI[j.statut] || j.statut : '-'}</td>
        </tr>`).join('')}
        </tbody>
      </table>
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-top:16px;font-size:12px">
        <div style="background:#f5f5f5;padding:10px;border-radius:6px"><strong>Jours travailles :</strong> ${r.presents}/${r.joursOuvres}</div>
        <div style="background:#f5f5f5;padding:10px;border-radius:6px"><strong>Absences :</strong> ${r.absences}</div>
        <div style="background:#f5f5f5;padding:10px;border-radius:6px"><strong>Retards :</strong> ${r.retards}</div>
        <div style="background:#f5f5f5;padding:10px;border-radius:6px"><strong>Heures totales :</strong> ${r.totalHeures}h</div>
        <div style="background:#f5f5f5;padding:10px;border-radius:6px"><strong>Heures sup :</strong> ${r.heuresSup}h</div>
        <div style="background:#f5f5f5;padding:10px;border-radius:6px"><strong>Conges :</strong> ${r.conges} j</div>
      </div>
      <div class="sig">
        <div>Agent<br/><br/><br/></div>
        <div>Superviseur<br/><br/><br/></div>
        <div>DG<br/>SOMABEY Akoété Dominique</div>
      </div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  const filtered = items.filter(p => {
    if (filterSite && p.site_id !== filterSite) return false;
    if (filterStatut && p.statut !== filterStatut) return false;
    return true;
  });

  const presents = items.filter(p => p.statut === 'present').length;
  const absents = items.filter(p => p.statut === 'absent').length;
  const retards = items.filter(p => p.statut === 'retard').length;
  const totalAgents = agents.filter(a => a.statut === 'actif').length;
  const tauxPresence = totalAgents > 0 ? Math.round((presents / totalAgents) * 100) : 0;

  const monthDays = getMonthDays(viewMonth.year, viewMonth.month);
  const monthLabel = new Date(viewMonth.year, viewMonth.month).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  const prevMonth = () => setViewMonth(v => v.month === 0 ? { year: v.year - 1, month: 11 } : { ...v, month: v.month - 1 });
  const nextMonth = () => setViewMonth(v => v.month === 11 ? { year: v.year + 1, month: 0 } : { ...v, month: v.month + 1 });

  const statCards = [
    { label: "Presents aujourd'hui", value: presents, color: '#2e7d32' },
    { label: 'Absents', value: absents, color: '#e53935' },
    { label: 'En retard', value: retards, color: '#f57c00' },
    { label: 'Taux presence', value: `${tauxPresence}%`, color: '#1565c0' },
  ];

  return (
    <div>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {statCards.map(sc => (
          <div key={sc.label} className="content-card" style={{ padding: '1.2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: sc.color }}>{sc.value}</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--gss-text-light)', marginTop: '0.25rem' }}>{sc.label}</div>
          </div>
        ))}
      </div>

      {/* Mini calendrier mensuel */}
      <div className="content-card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <button className="gss-btn-sm" onClick={prevMonth}>&larr;</button>
          <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{monthLabel}</span>
          <button className="gss-btn-sm" onClick={nextMonth}>&rarr;</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, textAlign: 'center', fontSize: 12 }}>
          {JOURS.map(j => <div key={j} style={{ fontWeight: 600, color: 'var(--gss-text-light)', padding: 4 }}>{j}</div>)}
          {Array.from({ length: monthDays[0]?.getDay() || 0 }).map((_, i) => <div key={`e-${i}`} />)}
          {monthDays.map(d => {
            const ds = d.toISOString().split('T')[0];
            const isToday = ds === todayStr();
            const isSelected = ds === filterDate;
            return (
              <div key={ds} onClick={() => setFilterDate(ds)}
                style={{
                  padding: '6px 0', borderRadius: 6, cursor: 'pointer',
                  background: isSelected ? '#8B1A1A' : isToday ? 'rgba(139,26,26,0.08)' : 'transparent',
                  color: isSelected ? '#fff' : isToday ? '#8B1A1A' : 'var(--gss-text)',
                  fontWeight: isToday || isSelected ? 700 : 400,
                  transition: 'all 0.15s',
                }}>
                {d.getDate()}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── CE QUE CHAQUE STATUT PRODUIT EN PAIE, LU AU REFERENTIEL ───────────
          ⛔ AUCUN EFFET N EST ECRIT ICI. `LIBELLE_EFFET` ne traduit que le code
             rendu par la base en francais lisible — il ne DECIDE rien. Un statut
             dont le pays n a pas declare l effet s affiche « a renseigner », et
             la paie le REFUSERA en nommant l agent : l ecran annonce donc a
             l avance ce que le controle pre-paie dira. */}
      {effets && Array.isArray(effets.statuts) && (
        <div className="content-card" style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
            Effet en paie de chaque statut — {effets.pays_code || 'pays non renseigné'}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {effets.statuts.map(st => {
              const aRenseigner = st.etat !== 'renseigne';
              return (
                <span key={st.statut} className="gss-badge" title={st.origine === 'tenant' ? 'Décision de cette entreprise' : 'Référentiel du pays'}
                  style={{
                    background: (STATUT_COLORS[st.statut] || '#999') + '18',
                    color: aRenseigner ? '#b71c1c' : (STATUT_COLORS[st.statut] || '#999'),
                    border: aRenseigner ? '1px solid #b71c1c' : '1px solid transparent',
                  }}>
                  <strong>{STATUT_LABELS[st.statut] || st.statut}</strong>
                  {' — '}
                  {st.etat === 'renseigne'
                    ? (LIBELLE_EFFET[st.effet] || st.effet)
                    : st.etat === 'regime_a_renseigner' ? 'régime à renseigner' : 'effet à renseigner'}
                  {st.origine === 'tenant' && ' ★'}
                </span>
              );
            })}
          </div>
          {effets.statuts.some(st => st.etat !== 'renseigne') && (
            <div style={{ fontSize: 11, color: '#b71c1c', marginTop: 6 }}>
              Les statuts en rouge ne sont pas paramétrés pour ce pays. Un bulletin
              employant l'un d'eux sera refusé, et l'agent nommé.
            </div>
          )}
          <div style={{ fontSize: 10, color: 'var(--gss-text-muted)', marginTop: 4 }}>
            ★ = décision propre à cette entreprise, en surcharge du référentiel du pays.
          </div>
        </div>
      )}

      {/* ── LA CONFIRMATION DE SORTIE, NOMMÉE ET DATÉE ───────────────────────
          ⛔ RIEN N'EST ÉCRIT TANT QUE CE PANNEAU N'EST PAS CONFIRMÉ. Il ne
             s'ouvre qu'après l'enregistrement du pointage, et il n'écrit que par
             `DELETE /agents/:id` — le point d'écriture de l'écran de sortie
             (file 100), qui exige la date ET le motif, trace l'acte et alerte
             quand le motif demande une procédure formelle.
          ⓘ La fiche n'est jamais supprimée : l'agent sort de l'effectif, pas de
            la base — historique de paie, attestations, conservation. */}
      {sortieAConfirmer && (
        <div className="content-card" style={{ marginBottom: '1rem', border: '2px solid #5d4037' }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#5d4037', marginBottom: 6 }}>
            Démission pointée — enregistrer la sortie de l'effectif ?
          </div>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            <strong>{sortieAConfirmer.matricule}</strong>, sortie au{' '}
            <strong>{formatDate(sortieAConfirmer.date)}</strong> — la date est celle
            du jour pointé. Le pointage est déjà enregistré ; cet acte-ci retire
            l'agent de l'effectif.
          </div>
          {motifsSortie === null ? (
            <div style={{ fontSize: 12, color: 'var(--gss-text-muted)' }}>Lecture des motifs…</div>
          ) : motifsSortie.length === 0 ? (
            <div style={{ fontSize: 12, color: '#b71c1c' }}>
              Aucun motif de sortie applicable n'a pu être lu. La sortie ne peut pas
              être enregistrée depuis cet écran.
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <select className="gss-input" style={{ width: 'auto', minWidth: 220 }}
                value={sortieMotif} onChange={e => setSortieMotif(e.target.value)}>
                <option value="">-- Motif de sortie --</option>
                {motifsSortie.map(m => (
                  <option key={m.code} value={m.code}>{m.libelle}</option>
                ))}
              </select>
              <button className="gss-btn gss-btn-primary" disabled={!sortieMotif || saving}
                onClick={confirmerSortie}>
                {saving ? 'Enregistrement…' : 'Enregistrer la sortie'}
              </button>
              <button className="gss-btn" disabled={saving}
                onClick={() => { setSortieAConfirmer(null); setSortieErreur(''); }}>
                Plus tard
              </button>
              {sortiesEnAttente.length > 0 && (
                <span style={{ fontSize: 11, color: 'var(--gss-text-muted)' }}>
                  {sortiesEnAttente.length} autre(s) démission(s) à confirmer ensuite,
                  une par une.
                </span>
              )}
            </div>
          )}
          {sortieErreur && (
            <div style={{ fontSize: 12, color: '#b71c1c', marginTop: 6 }}>{sortieErreur}</div>
          )}
          <div style={{ fontSize: 11, color: 'var(--gss-text-muted)', marginTop: 6 }}>
            « Plus tard » ne perd rien : le pointage reste, et l'agent reste dans
            l'effectif — donc payé le mois suivant — jusqu'à ce que la sortie soit
            enregistrée.
          </div>
        </div>
      )}
      {sortieFaite && (
        <div className="content-card" style={{ marginBottom: '1rem', border: '1px solid #2e7d32' }}>
          <span style={{ fontSize: 13, color: '#2e7d32' }}>{sortieFaite}</span>
        </div>
      )}

      <div className="content-card">
        <div className="gss-toolbar">
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input className="gss-input" type="date" style={{ width: 'auto' }} value={filterDate} onChange={e => setFilterDate(e.target.value)} />
            <select className="gss-input" style={{ width: 'auto', minWidth: 140 }} value={filterSite} onChange={e => setFilterSite(e.target.value)}>
              <option value="">Tous les sites</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
            <select className="gss-input" style={{ width: 'auto', minWidth: 130 }} value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
              <option value="">Tous statuts</option>
              {Object.entries(STATUT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <span style={{ color: 'var(--gss-text-light)', fontSize: '0.85rem' }}>{filtered.length} pointage(s)</span>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="gss-btn" onClick={handleExport}>Export CSV</button>
            <select className="gss-input" style={{ width: 'auto', minWidth: 160 }} onChange={e => { if (e.target.value) openFiche(e.target.value); e.target.value = ''; }}>
              <option value="">Fiche mensuelle...</option>
              {agents.filter(a => a.statut === 'actif').map(a => <option key={a.id} value={a.id}>{a.prenom} {a.nom}</option>)}
            </select>
            {/* TODO: Intégrer pointage par QR code / badge NFC (phase mobile) */}
            {peutImporter && (
              <button className="gss-btn" onClick={() => setShowImport(true)} style={{ padding: '8px 16px', fontSize: 13 }}>
                Importer le mois
              </button>
            )}
            <button className="gss-btn gss-btn-primary" onClick={openBulk} style={{ padding: '8px 16px', fontSize: 13 }}>Pointer</button>
          </div>
        </div>

        <div className="content-card-title">Pointage du {formatDate(filterDate)}</div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
        ) : filtered.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDDDDD" strokeWidth="1.5" style={{ marginBottom: 16 }}>
              <rect x="2" y="3" width="20" height="18" rx="2"/><line x1="8" y1="1" x2="8" y2="5"/><line x1="16" y1="1" x2="16" y2="5"/><line x1="2" y1="9" x2="22" y2="9"/>
            </svg>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#666', marginBottom: 4 }}>Aucun enregistrement</div>
            <div style={{ fontSize: 14, color: '#999' }}>Aucun pointage pour cette date</div>
          </div>
        ) : (
          <div className="gss-table-wrap" style={{ overflowX: 'scroll' }}>
            <table className="gss-table" style={{ minWidth: 1000 }}>
              <thead><tr><th>Agent</th><th>Heure arrivee</th><th>Heure depart</th><th>Site</th><th>Statut</th><th style={{ width: 200 }}>Actions</th></tr></thead>
              <tbody>
                {filtered.map(p => (
                  editRow === p.id ? (
                    <tr key={p.id} style={{ background: '#fffde7' }}>
                      <td><strong>{agentName(p.agent_id)}</strong></td>
                      <td><input type="time" className="gss-input" style={{ padding: '4px 6px', fontSize: 12, width: 100 }} value={editForm.heure_arrivee} onChange={e => setEditForm(f => ({ ...f, heure_arrivee: e.target.value }))} /></td>
                      <td><input type="time" className="gss-input" style={{ padding: '4px 6px', fontSize: 12, width: 100 }} value={editForm.heure_depart} onChange={e => setEditForm(f => ({ ...f, heure_depart: e.target.value }))} /></td>
                      <td>
                        <input className="gss-input" placeholder="Observations" style={{ padding: '4px 6px', fontSize: 12, width: 120 }} value={editForm.observations} onChange={e => setEditForm(f => ({ ...f, observations: e.target.value }))} />
                      </td>
                      <td>
                        <select className="gss-input" style={{ padding: '4px 6px', fontSize: 12, width: 100 }} value={editForm.statut} onChange={e => setEditForm(f => ({ ...f, statut: e.target.value }))}>
                          {Object.entries(STATUT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button onClick={saveEditRow} disabled={saving} style={{ padding: '4px 10px', fontSize: 12, background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', marginRight: 4 }}>
                          {saving ? '...' : 'Sauver'}
                        </button>
                        <button onClick={() => deletePointage(p)} style={{ padding: '4px 10px', fontSize: 12, background: '#e53935', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', marginRight: 4 }}>
                          Supprimer
                        </button>
                        <button onClick={() => setEditRow(null)} style={{ padding: '4px 10px', fontSize: 12, background: '#eee', color: '#555', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                          ✕
                        </button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={p.id}>
                      <td><strong>{agentName(p.agent_id)}</strong></td>
                      <td>{formatTime(p.heure_arrivee)}</td>
                      <td>{formatTime(p.heure_depart)}</td>
                      <td>{siteName(p.site_id)}</td>
                      <td><span className="gss-badge" style={{ background: (STATUT_COLORS[p.statut] || '#999') + '20', color: STATUT_COLORS[p.statut] || '#999' }}>{STATUT_LABELS[p.statut] || p.statut}</span></td>
                      <td>
                        <button onClick={() => openEditRow(p)} style={{ padding: '4px 10px', fontSize: 12, background: '#fff3e0', color: '#f57c00', border: '1px solid #f57c00', borderRadius: 4, cursor: 'pointer' }}>
                          Modifier
                        </button>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showImport && (
        <ImportPointageModale
          /* ⓘ LE MOIS IMPORTÉ EST CELUI QUE L ÉCRAN AFFICHE (`viewMonth`), pas
           *   le mois courant : on importe le mois qu on regarde. `+ 1` parce
           *   que `Date` compte les mois à partir de 0 et que le produit, lui,
           *   les compte à partir de 1 — même convention que `openFiche`. */
          mois={viewMonth.month + 1}
          annee={viewMonth.year}
          onFerme={() => setShowImport(false)}
          /* ⓘ L ecran se recharge APRES une ecriture reussie : sans cela, la
           *   liste affichee resterait celle d avant l import. */
          onEcrit={() => load()}
        />
      )}

      {showBulk && (
        <div className="gss-modal-overlay" onClick={() => setShowBulk(false)}>
          <div className="gss-modal" style={{ maxWidth: 900, width: '95vw' }} onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <h2>Pointage en masse — {formatDate(filterDate)}</h2>
              </div>
              <button className="gss-modal-close" onClick={() => setShowBulk(false)}>&times;</button>
            </div>
            {error && <div style={{ padding: '0.5rem 1.5rem', color: '#e53935', fontSize: '0.9rem' }}>{error}</div>}
            <div style={{ padding: '1rem 1.5rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: 16 }}>
                <select className="gss-input" style={{ width: 'auto', minWidth: 180 }} value={bulkSite} onChange={e => setBulkSite(e.target.value)}>
                  <option value="">-- Selectionner un site --</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                </select>
                <button className="gss-btn gss-btn-primary" style={{ padding: '8px 16px', fontSize: 13 }} onClick={loadBulkAgents}>
                  Charger les agents
                </button>
                {bulkRows.length > 0 && (
                  <button className="gss-btn" style={{ padding: '8px 16px', fontSize: 13 }} onClick={markAllPresent}>
                    Marquer tous Presents
                  </button>
                )}
              </div>

              {bulkRows.length > 0 && (
                <div style={{ maxHeight: '50vh', overflowY: 'auto', overflowX: 'auto' }}>
                  <table className="gss-table" style={{ fontSize: 12, minWidth: 700 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '6px 8px' }}>Agent</th>
                        <th style={{ padding: '6px 8px', width: 90 }}>Arrivee</th>
                        <th style={{ padding: '6px 8px', width: 90 }}>Depart</th>
                        <th style={{ padding: '6px 8px' }}>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkRows.map((r, i) => (
                        <tr key={r.agent_id}>
                          <td style={{ padding: '4px 8px', fontWeight: 500 }}>{r.matricule} — {r.prenom} {r.nom}</td>
                          <td style={{ padding: '4px 8px' }}>
                            <input type="time" className="gss-input" style={{ padding: '4px 6px', fontSize: 12 }} value={r.heure_arrivee} onChange={e => setBulkField(i, 'heure_arrivee', e.target.value)} />
                          </td>
                          <td style={{ padding: '4px 8px' }}>
                            <input type="time" className="gss-input" style={{ padding: '4px 6px', fontSize: 12 }} value={r.heure_depart} onChange={e => setBulkField(i, 'heure_depart', e.target.value)} />
                          </td>
                          <td style={{ padding: '4px 4px' }}>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {Object.entries(STATUT_LABELS).map(([k, v]) => (
                                <label key={k} style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 11,
                                  padding: '3px 6px', borderRadius: 4, fontWeight: r.statut === k ? 700 : 400,
                                  background: r.statut === k ? STATUT_COLORS[k] + '20' : 'transparent',
                                  color: r.statut === k ? STATUT_COLORS[k] : '#888',
                                }}>
                                  <input type="radio" name={`statut-${i}`} checked={r.statut === k} onChange={() => setBulkField(i, 'statut', k)} style={{ width: 12, height: 12 }} />
                                  {v}
                                </label>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {bulkRows.length === 0 && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#999', fontSize: 13 }}>
                  Selectionnez un site puis cliquez "Charger les agents"
                </div>
              )}
            </div>
            {bulkRows.length > 0 && (
              <div className="gss-modal-actions">
                <span style={{ fontSize: 12, color: 'var(--gss-text-light)' }}>{bulkRows.length} agent(s)</span>
                <button className="gss-btn-cancel" onClick={() => setShowBulk(false)}>Annuler</button>
                <button className="gss-btn-deploy" onClick={handleBulkSubmit} disabled={saving}>
                  {saving ? 'Enregistrement...' : 'Valider tout le site'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Fiche mensuelle */}
      {ficheData && (
        <div className="gss-modal-overlay" onClick={() => { setFicheData(null); setFicheAgent(null); }}>
          <div className="gss-modal gss-modal-lg" style={{ maxWidth: 900 }} onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div>
                <h2>Fiche pointage — {ficheData.agent.prenom} {ficheData.agent.nom}</h2>
                <div className="gss-modal-subtitle">{ficheData.agent.matricule} — {['','Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'][ficheData.mois]} {ficheData.annee}</div>
              </div>
              <button className="gss-modal-close" onClick={() => { setFicheData(null); setFicheAgent(null); }}>&times;</button>
            </div>
            <div style={{ padding: '1rem 1.5rem', maxHeight: '60vh', overflowY: 'auto' }}>
              <div className="gss-table-wrap">
                <table className="gss-table" style={{ fontSize: 12 }}>
                  <thead><tr><th>Jour</th><th>Date</th><th>Arrivee</th><th>Depart</th><th>Heures</th><th>Statut</th></tr></thead>
                  <tbody>
                    {ficheData.jours.map(j => (
                      <tr key={j.jour} style={{ background: j.dimanche ? 'var(--gss-bg)' : 'transparent' }}>
                        <td>{j.jour}</td>
                        <td>{['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'][j.dow]}</td>
                        <td>{j.arrivee || '-'}</td>
                        <td>{j.depart || '-'}</td>
                        <td>{j.heures || '-'}</td>
                        <td>
                          {j.statut ? (
                            <span className="gss-badge" style={{ background: (STATUT_COLORS[j.statut] || '#999') + '20', color: STATUT_COLORS[j.statut] || '#999' }}>
                              {STATUT_LABELS[j.statut] || j.statut}
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginTop: 12 }}>
                {[
                  { l: 'Jours travailles', v: `${ficheData.recap.presents}/${ficheData.recap.joursOuvres}`, c: '#2e7d32' },
                  { l: 'Absences', v: ficheData.recap.absences, c: '#e53935' },
                  { l: 'Retards', v: ficheData.recap.retards, c: '#f57c00' },
                  { l: 'Heures totales', v: `${ficheData.recap.totalHeures}h`, c: '#1565c0' },
                  { l: 'Heures sup', v: `${ficheData.recap.heuresSup}h`, c: '#7b1fa2' },
                  { l: 'Conges', v: `${ficheData.recap.conges} j`, c: '#00897b' },
                ].map(s => (
                  <div key={s.l} style={{ background: 'var(--gss-bg)', padding: 10, borderRadius: 6, textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: 'var(--gss-text-light)' }}>{s.l}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: s.c }}>{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="gss-modal-actions">
              <button className="gss-btn" onClick={() => { setFicheData(null); setFicheAgent(null); }}>Fermer</button>
              <button className="gss-btn gss-btn-primary" onClick={exportFichePdf}>Exporter PDF</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
